import { useEffect } from "react";

/**
 * useCameraLifecycle — groups the many side-effect hooks that manage
 * camera initialisation, video playback, facingMode persistence,
 * multiple-camera detection, responsive sizing, and cleanup.
 *
 * Pulling these out of Camera.jsx removes ~180 lines of useEffect blocks.
 */
export default function useCameraLifecycle({
	// camera stream hook — pass the entire return object
	cameraStream: {
		videoRef,
		streamRef,
		stream,
		hasPermission,
		isInitializing,
		setError,
		setStream,
		setHasPermission,
		setIsInitializing,
		startCamera,
		stopCamera,
	},
	// recording state
	recording: {
		isRecording,
		isAudioRecording,
		stopRecording,
		stopAudioRecording,
	},
	// camera config
	mode,
	facingMode,
	delayInitialization,
	autoTriggerUpload,
	// callbacks & refs
	cleanupAudio,
	cameraContainerRef,
	handleTrackEndedRef,
	setHasMultipleCameras,
	setRecordingDuration,
	duckBgmForMode,
}) {
	// ── Duck BGM when mode changes ───────────────────────────────────────
	useEffect(() => {
		duckBgmForMode(mode);
	}, [mode, duckBgmForMode]);

	// ── Recording duration timer ─────────────────────────────────────────
	useEffect(() => {
		let interval;
		if (isRecording || isAudioRecording) {
			interval = setInterval(() => {
				setRecordingDuration((prev) => prev + 1);
			}, 1000);
		} else {
			setRecordingDuration(0);
		}
		return () => clearInterval(interval);
	}, [isRecording, isAudioRecording, setRecordingDuration]);

	// ── Cleanup on unmount ───────────────────────────────────────────────
	useEffect(() => {
		const sRef = streamRef;
		const vRef = videoRef;
		return () => {
			if (sRef.current) {
				sRef.current.getTracks().forEach((track) => track.stop());
				sRef.current = null;
			}
			if (vRef.current) {
				vRef.current.srcObject = null;
			}
			cleanupAudio();
			stopRecording();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ── Save facingMode preference ───────────────────────────────────────
	useEffect(() => {
		localStorage.setItem("cameraFacingMode", facingMode);
	}, [facingMode]);

	// ── Check for multiple cameras ───────────────────────────────────────
	// Mobile devices always have a front + back camera, and `enumerateDevices`
	// only returns useful labels AFTER getUserMedia permission is granted. If
	// this effect ran before permission, the label-based detection returned
	// empty labels → hasFront/hasBack both false → flip button didn't show.
	// Just assume `true` on mobile and only probe on desktop.
	useEffect(() => {
		if (delayInitialization || autoTriggerUpload || mode === "audio") {
			return;
		}
		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		if (isMobile) {
			setHasMultipleCameras(true);
			return;
		}
		const check = async () => {
			try {
				const devices = await navigator.mediaDevices.enumerateDevices();
				const vids = devices.filter((d) => d.kind === "videoinput");
				setHasMultipleCameras(vids.length > 1);
			} catch {
				setHasMultipleCameras(false);
			}
		};
		check();
	}, [mode, delayInitialization, autoTriggerUpload, setHasMultipleCameras]);

	// ── Hide navbar while camera is active ───────────────────────────────
	useEffect(() => {
		document.body.classList.add("camera-active");
		return () => document.body.classList.remove("camera-active");
	}, []);

	// ── Initialize camera stream ─────────────────────────────────────────
	useEffect(() => {
		if (delayInitialization) {
			setIsInitializing(false);
			setHasPermission(false);
			return;
		}
		if (autoTriggerUpload || mode === "audio") {
			setIsInitializing(false);
			setHasPermission(true);
		} else {
			startCamera();
		}
		return () => {
			if (!autoTriggerUpload && mode !== "audio" && !delayInitialization) {
				stopCamera();
			}
		};
	}, [
		facingMode,
		mode,
		autoTriggerUpload,
		delayInitialization,
		startCamera,
		stopCamera,
		setIsInitializing,
		setHasPermission,
	]);

	// ── Keep video transform in sync with facingMode ─────────────────────
	useEffect(() => {
		if (videoRef.current && streamRef.current) {
			videoRef.current.style.transform =
				facingMode === "user" ? "scaleX(-1)" : "none";
		}
	}, [facingMode, videoRef, streamRef]);

	// ── Ensure video plays when stream is ready ──────────────────────────
	useEffect(() => {
		if (
			videoRef.current &&
			streamRef.current &&
			hasPermission &&
			!isInitializing
		) {
			const video = videoRef.current;
			const currentStream = streamRef.current;

			if (video.srcObject !== currentStream) {
				video.srcObject = currentStream;
				video.style.transform = facingMode === "user" ? "scaleX(-1)" : "none";
			}
			video.muted = true;
			video.playsInline = true;
			video.autoplay = true;

			const handleVideoPause = () => {
				if (video.paused && streamRef.current) {
					const tracks = streamRef.current.getVideoTracks();
					const active = tracks.filter((t) => t.readyState === "live");
					if (active.length === 0 && tracks.length > 0) {
						setError(
							"Camera connection lost. Please check your camera connection and try again."
						);
						setHasPermission(false);
					}
				}
			};
			video.addEventListener("pause", handleVideoPause);

			const tryPlay = async () => {
				try {
					if (video.paused) {
						await video.play();
					}
				} catch {
					setTimeout(async () => {
						try {
							if (video.paused) {
								await video.play();
							}
						} catch {
							/* user can retry */
						}
					}, 200);
				}
			};

			if (video.readyState >= 2) {
				tryPlay();
			} else {
				const onLoaded = () => {
					tryPlay();
					video.removeEventListener("loadedmetadata", onLoaded);
				};
				video.addEventListener("loadedmetadata", onLoaded);
				setTimeout(tryPlay, 500);
			}

			return () => {
				video.removeEventListener("pause", handleVideoPause);
			};
		}
	}, [
		stream,
		hasPermission,
		isInitializing,
		facingMode,
		videoRef,
		streamRef,
		setError,
		setHasPermission,
	]);

	// (Intentionally removed) A separate "restart camera when facing mode
	// toggles" effect used to live here and call stopCamera()+startCamera()
	// on every facingMode change. The init effect above already depends on
	// `facingMode`, so it fires its own cleanup + re-run on flip — this
	// second effect was racing with it, stopping the freshly-opened stream
	// right after getUserMedia resolved and leaving the camera suspended.

	// ── Track-ended handler ──────────────────────────────────────────────
	useEffect(() => {
		handleTrackEndedRef.current = (track) => {
			if (track.kind === "audio") {
				if (isRecording) {
					stopRecording();
				}
				if (isAudioRecording) {
					stopAudioRecording();
				}
			}
		};
	}, [isRecording, isAudioRecording, stopRecording, stopAudioRecording]);

	// ── Responsive camera container sizing ───────────────────────────────
	useEffect(() => {
		const update = () => {
			if (!cameraContainerRef.current) {
				return;
			}
			const isDesktop = window.innerWidth >= 768;
			if (isDesktop) {
				const w = `calc(100vh * 9 / 16)`;
				cameraContainerRef.current.style.cssText = `
					position: relative; height: 100% !important;
					width: ${w} !important; max-width: calc(100vw - 2rem) !important;
					margin-left: auto; margin-right: auto;`;
			} else {
				cameraContainerRef.current.style.cssText = `
					position: relative; width: 100% !important;
					height: 100% !important; max-width: 100% !important;`;
			}
		};
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, [cameraContainerRef]);
}
